import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  image_url: string | null;
  author: string;
  created_at: string;
}

const BlogPost = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchPost(id);
    }
  }, [id]);

  const fetchPost = async (slug: string) => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();

      if (error) throw error;
      setPost(data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 md:py-16 px-4 text-center">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto py-8 md:py-16 px-4 text-center">
        <h1 className="text-xl md:text-3xl font-bold mb-4">Blog Post Not Found</h1>
        <Link to="/blog">
          <Button className="text-sm md:text-base">Back to Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - Junnior's Tacos Blog</title>
        <meta name="description" content={post.title} />
      </Helmet>
      
      <main>
        {/* Hero Image */}
        {post.image_url && (
          <div className="relative h-[400px] w-full">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}

        {/* Article Content */}
        <article className="container mx-auto max-w-4xl px-4 -mt-20 relative z-10">
          <div className="bg-background rounded-lg shadow-xl p-8 md:p-12">
            <Link to="/blog" className="inline-flex items-center text-primary hover:underline mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <div className="flex items-center text-sm text-muted-foreground mb-4">
              <Calendar className="w-4 h-4 mr-2" />
              {formatDate(post.created_at)}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold font-['Playfair_Display'] mb-8">
              {post.title}
            </h1>

            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <div className="mt-12 pt-8 border-t">
              <Link to="/blog">
                <Button variant="outline" className="w-full md:w-auto">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to All Posts
                </Button>
              </Link>
            </div>
          </div>
        </article>

        {/* Bottom Spacing */}
        <div className="h-16" />
      </main>
    </>
  );
};

export default BlogPost;
